export type BlockSelectionTarget = 'wrapper' | 'content' | 'frame';
export type BlockSizingMode = 'content-sized' | 'frame-sized' | 'fixed-protected';

export interface BlockPolicy {
  type: string;
  sizingMode: BlockSizingMode;
  selectionTarget: BlockSelectionTarget;
  canMove: boolean;
  canResize: boolean;
  canDelete: boolean;
  canDuplicate?: boolean;
  rejectOverlapWithProtected?: boolean;
}

const DEFAULT_POLICY: BlockPolicy = {
  type: 'default',
  sizingMode: 'frame-sized',
  selectionTarget: 'wrapper',
  canMove: true,
  canResize: true,
  canDelete: true,
  canDuplicate: true,
  rejectOverlapWithProtected: true,
};

export const RUNTIME_BLOCK_POLICIES: Record<string, BlockPolicy> = {
  // Add new runtime block types here.
  // Keep this in sync with:
  // - admin overrides in src/components/admin/pageLayout/blockPolicies/editor.ts
  // - layout validation schemas in src/lib/layoutValidation.ts
  default: DEFAULT_POLICY,
  text: {
    type: 'text',
    sizingMode: 'content-sized',
    selectionTarget: 'content',
    canMove: true,
    canResize: true,
    canDelete: true,
    canDuplicate: true,
    rejectOverlapWithProtected: true,
  },
  image: {
    type: 'image',
    sizingMode: 'frame-sized',
    selectionTarget: 'content',
    canMove: true,
    canResize: true,
    canDelete: true,
    canDuplicate: true,
    rejectOverlapWithProtected: true,
  },
  'decorative-asset': {
    type: 'decorative-asset',
    sizingMode: 'frame-sized',
    selectionTarget: 'content',
    canMove: true,
    canResize: true,
    canDelete: true,
    canDuplicate: true,
    rejectOverlapWithProtected: true,
  },
  gallery: {
    type: 'gallery',
    sizingMode: 'frame-sized',
    selectionTarget: 'frame',
    canMove: true,
    canResize: true,
    canDelete: true,
    canDuplicate: true,
    rejectOverlapWithProtected: true,
  },
  'site-progress': {
    type: 'site-progress',
    sizingMode: 'frame-sized',
    selectionTarget: 'frame',
    canMove: true,
    canResize: true,
    canDelete: true,
    canDuplicate: true,
    rejectOverlapWithProtected: true,
  },
  'site-footer': {
    type: 'site-footer',
    sizingMode: 'frame-sized',
    selectionTarget: 'frame',
    canMove: true,
    canResize: true,
    canDelete: true,
    canDuplicate: true,
    rejectOverlapWithProtected: true,
  },
  'request-form': {
    type: 'request-form',
    sizingMode: 'frame-sized',
    selectionTarget: 'frame',
    canMove: true,
    canResize: true,
    canDelete: true,
    canDuplicate: true,
    rejectOverlapWithProtected: true,
  },
  'product-card': {
    type: 'product-card',
    sizingMode: 'frame-sized',
    selectionTarget: 'frame',
    canMove: true,
    canResize: true,
    canDelete: true,
    canDuplicate: true,
    rejectOverlapWithProtected: true,
  },
  'model-viewer': {
    type: 'model-viewer',
    sizingMode: 'frame-sized',
    selectionTarget: 'frame',
    canMove: true,
    canResize: true,
    canDelete: true,
    canDuplicate: true,
    rejectOverlapWithProtected: true,
  },
  'product-deconstructor': {
    type: 'product-deconstructor',
    sizingMode: 'frame-sized',
    selectionTarget: 'frame',
    canMove: true,
    canResize: true,
    canDelete: true,
    canDuplicate: true,
    rejectOverlapWithProtected: true,
  },
  'customiser-viewer': {
    type: 'customiser-viewer',
    sizingMode: 'frame-sized',
    selectionTarget: 'frame',
    canMove: true,
    canResize: true,
    canDelete: true,
    canDuplicate: true,
    rejectOverlapWithProtected: true,
  },
  'customiser-finish-picker': {
    type: 'customiser-finish-picker',
    sizingMode: 'frame-sized',
    selectionTarget: 'frame',
    canMove: true,
    canResize: true,
    canDelete: true,
    canDuplicate: true,
    rejectOverlapWithProtected: true,
  },
  'customiser-colour-filter': {
    type: 'customiser-colour-filter',
    sizingMode: 'frame-sized',
    selectionTarget: 'frame',
    canMove: true,
    canResize: true,
    canDelete: true,
    canDuplicate: true,
    rejectOverlapWithProtected: true,
  },
  'customiser-combination-results': {
    type: 'customiser-combination-results',
    sizingMode: 'frame-sized',
    selectionTarget: 'frame',
    canMove: true,
    canResize: true,
    canDelete: true,
    canDuplicate: true,
    rejectOverlapWithProtected: true,
  },
  button: {
    type: 'button',
    sizingMode: 'content-sized',
    selectionTarget: 'content',
    canMove: true,
    canResize: true,
    canDelete: true,
    canDuplicate: true,
    rejectOverlapWithProtected: true,
  },
  protected: {
    type: 'protected',
    sizingMode: 'fixed-protected',
    selectionTarget: 'frame',
    canMove: true,
    canResize: true,
    canDelete: false,
    canDuplicate: false,
    rejectOverlapWithProtected: true,
  },
  'protected-customiser-region': {
    type: 'protected-customiser-region',
    sizingMode: 'fixed-protected',
    selectionTarget: 'frame',
    canMove: true,
    canResize: true,
    canDelete: false,
    canDuplicate: false,
    rejectOverlapWithProtected: true,
  },
  'protected-locked': {
    type: 'protected-locked',
    sizingMode: 'fixed-protected',
    selectionTarget: 'frame',
    canMove: false,
    canResize: false,
    canDelete: false,
    canDuplicate: false,
    rejectOverlapWithProtected: true,
  },
};

export function getRuntimeBlockPolicy(type: string): BlockPolicy {
  return RUNTIME_BLOCK_POLICIES[type] ?? RUNTIME_BLOCK_POLICIES.default;
}
